import React from 'react';
import { StateMandatedReportContext } from '../../../../../Main';
import { ItemListConfig, ItemValueType } from '../../../../../../../Models';
import {
  checkSectionsFields,
  getSectionsDefaultValue,
  handleNeedHelpScroll,
  getFirstInvalidFieldPath
} from '../../../../../../../Helpers';
import { useNeedHelpTarget } from '../../../Contexts/NeedHelpContext';
import { isEqualObject } from 'ui-smartforce-settings';

export interface UseOtherAgenciesInvolvedFormViewParams {
  config: ItemListConfig;
  agencies: ItemValueType[];
  sectionName?: string;
  onChange: (agencies: ItemValueType[]) => void;
}

export interface UseOtherAgenciesInvolvedFormViewResult {
  showStateRelated: boolean;
  selectedAgency: number;
  agencyValue: ItemValueType;
  isOtherAgencyPanelOpen: boolean;
  isAgencySaveDisabled: boolean;
  isAgencyMissingInformation: boolean;
  onAddAgency: () => void;
  onSelectAgency: (index: number) => void;
  onDuplicateAgency: (index: number) => void;
  onRemoveAgency: (index: number) => void;
  onSaveAgency: () => void;
  onCloseAgencyPanel: () => void;
  onItemFormChange: (value: ItemValueType) => void;
  onNeedHelp: () => void;
}

export const useOtherAgenciesInvolvedFormView = ({
  config,
  agencies,
  sectionName,
  onChange
}: UseOtherAgenciesInvolvedFormViewParams): UseOtherAgenciesInvolvedFormViewResult => {
  const { setNeedHelpTargetPath } = useNeedHelpTarget();
  const showStateRelated: boolean = React.useContext(
    StateMandatedReportContext
  );

  const [isOtherAgencyPanelOpen, setIsOtherAgencyPanelOpen] =
    React.useState<boolean>(false);

  const [selectedAgency, setSelectedAgency] = React.useState<number>(-1);

  const [agencyValue, setAgencyValue] = React.useState<ItemValueType>(
    getSectionsDefaultValue(config.sections)
  );

  const onAddAgency = () => {
    setAgencyValue(getSectionsDefaultValue(config.sections));
    setIsOtherAgencyPanelOpen(true);
  };

  const onSelectAgency = (index: number) => {
    setSelectedAgency(index);
    setAgencyValue(agencies[index]);
    setIsOtherAgencyPanelOpen(true);
  };

  const onDuplicateAgency = (index: number) => {
    const selected = agencies[index];

    if (!selected) return;

    onChange([...agencies, selected]);
  };

  const onRemoveAgency = (index: number) => {
    let newList: ItemValueType[] = agencies.filter((_, i) => i !== index);

    onChange(newList);
  };

  const onSaveAgency = () => {
    let newList: ItemValueType[];

    if (selectedAgency === -1) {
      newList = [...agencies, agencyValue];
    } else {
      newList = [
        ...agencies.slice(0, selectedAgency),
        agencyValue,
        ...agencies.slice(selectedAgency + 1)
      ];
    }

    onChange(newList);
    onCloseAgencyPanel();
  };

  const onCloseAgencyPanel = () => {
    setNeedHelpTargetPath(null);
    setSelectedAgency(-1);
    setIsOtherAgencyPanelOpen(false);
  };

  const onItemFormChange = (value: ItemValueType) => {
    setAgencyValue(value);
  };

  const onNeedHelp = () => {
    const cb = (path: string) => {
      setNeedHelpTargetPath(path);
    };

    handleNeedHelpScroll(config, agencyValue, sectionName, cb);
  };

  const isAgencySaveDisabled: boolean =
    !checkSectionsFields(config, agencyValue) ||
    (selectedAgency !== -1 &&
      isEqualObject<ItemValueType>(agencies[selectedAgency], agencyValue));

  const isAgencyMissingInformation =
    selectedAgency !== -1 && !checkSectionsFields(config, agencyValue);

  // Clear help target when value becomes fully valid
  React.useEffect(() => {
    const isStillInvalid = getFirstInvalidFieldPath(config, agencyValue);
    if (!isStillInvalid) {
      setNeedHelpTargetPath(null);
    }
  }, [agencyValue, config, setNeedHelpTargetPath]);

  return {
    showStateRelated,
    selectedAgency,
    agencyValue,
    isOtherAgencyPanelOpen,
    isAgencySaveDisabled,
    isAgencyMissingInformation,
    onAddAgency,
    onSelectAgency,
    onDuplicateAgency,
    onRemoveAgency,
    onSaveAgency,
    onCloseAgencyPanel,
    onItemFormChange,
    onNeedHelp
  };
};
